import { TextSkeleton } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Stack } from "@mui/material";
import { CancellationCreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CancellationCreditProgressBar";
import { formatDurationRangeLabel } from "@src/appV2/redesign/utils/formatDurationLabel";

import { ScoreBreakdownList } from "./components/ScoreBreakdownList";
import { ScoreDisplay } from "./components/ScoreDisplay";
import { ShiftCancelFooter } from "./components/ShiftCancelFooter";
import { ShiftCancelHeader } from "./components/ShiftCancelHeader";
import type { ShiftCancelPageData } from "./hooks/useShiftCancelPageData";

export interface ShiftCancelPageContentHandlers {
  isCancelling: boolean;
  isCreditPreviewed: boolean;
  onKeepShift: () => void;
  onPressAndHoldToCancel: () => void;
  onPreviewCredit: () => void;
  onWhyCantIUseCredit?: () => void;
}

export type ShiftCancelPageContentProps = ShiftCancelPageData & ShiftCancelPageContentHandlers;

function ShiftCancelPageContentSkeleton(): JSX.Element {
  return (
    <>
      <ShiftCancelHeader />
      <Stack spacing={4} alignItems="center" sx={{ paddingY: 8, paddingX: 6 }}>
        <TextSkeleton variant="h2" width="60%" />
        <TextSkeleton variant="body2" width="80%" />
        <TextSkeleton variant="h1" width="30%" />
        <TextSkeleton variant="body2" width="100%" />
        <TextSkeleton variant="body2" width="100%" />
      </Stack>
    </>
  );
}

export function ShiftCancelPageContent(props: ShiftCancelPageContentProps): JSX.Element | null {
  if (!props.enabled || props.isLoading) {
    return <ShiftCancelPageContentSkeleton />;
  }

  if (props.isError) {
    return (
      <>
        <ShiftCancelHeader />
        <Alert severity="error" sx={{ margin: 4 }}>
          Something went wrong while loading cancellation details. Please try again.
        </Alert>
      </>
    );
  }

  const {
    cancellationPolicy,
    currentScore,
    baseScoreChange,
    newScoreBeforeCredit,
    creditBenefit,
    finalScore,
    initialECStats,
    finalECStats,
    hasCancellationCredit,
    isWithinECWindow,
    canUseCredit,
    isCancelling,
    isCreditPreviewed,
    onKeepShift,
    onPressAndHoldToCancel,
    onPreviewCredit,
    onWhyCantIUseCredit,
  } = props;

  const isPendingPreview = canUseCredit && !isCreditPreviewed;

  const [from, to] = cancellationPolicy?.leadTime ?? [0, undefined];
  const policyLabel = `${formatDurationRangeLabel(from ?? 0, to ?? undefined, "hour", {
    format: "abbreviated",
    keepOriginalUnit: true,
  })} cancellation`;
  const showWhyCantIUseCredit =
    !canUseCredit && hasCancellationCredit && typeof onWhyCantIUseCredit === "function";

  return (
    <>
      <ShiftCancelHeader />

      {isDefined(initialECStats) && isDefined(finalECStats) && (
        <Stack sx={{ flex: 1, paddingY: 8 }}>
          <ScoreDisplay
            score={isPendingPreview ? newScoreBeforeCredit : finalScore}
            variant={isCreditPreviewed ? "credit-applied" : "penalized"}
          />

          {/* Only render progress bar for <24h cancellations */}
          {isWithinECWindow && (
            <Stack sx={{ paddingX: 6, paddingY: 4 }}>
              <CancellationCreditProgressBar
                showShiftsProgress={!hasCancellationCredit}
                cancellationCreditStats={isCreditPreviewed ? finalECStats : initialECStats}
                mode={isCreditPreviewed ? "drain" : "static"}
                label={isCreditPreviewed ? "Applying credit..." : undefined}
              />
            </Stack>
          )}

          <ScoreBreakdownList
            currentScore={currentScore}
            baseScoreChange={baseScoreChange}
            policyLabel={policyLabel}
            creditBenefit={isCreditPreviewed ? creditBenefit : undefined}
          />
        </Stack>
      )}

      <ShiftCancelFooter
        variant={isPendingPreview ? "preview-credit" : "cancel-shift"}
        showWhyCantIUseCredit={showWhyCantIUseCredit}
        isCancelling={isCancelling}
        onKeepShift={onKeepShift}
        onPressAndHoldToCancel={onPressAndHoldToCancel}
        onPreviewCredit={onPreviewCredit}
        onWhyCantIUseCredit={onWhyCantIUseCredit}
      />
    </>
  );
}
